import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Palette, Upload, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Organization branding & subdomain configuration.
 * Corporate plan only — the parent OrgAdmin page already restricts this route.
 */
export default function BrandingSection({ org, isAdmin }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [slug, setSlug] = useState(org?.slug || "");
  const [color, setColor] = useState(org?.branding?.primary_color || "#2563eb");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setSlug(org?.slug || "");
    setColor(org?.branding?.primary_color || "#2563eb");
  }, [org?.slug, org?.branding?.primary_color]);

  const saveMutation = useMutation({
    mutationFn: async (body) => (await api.patch("/org/branding", body)).data,
    onSuccess: () => {
      toast.success("Marca actualizada");
      qc.invalidateQueries({ queryKey: ["org-me"] });
      qc.invalidateQueries({ queryKey: ["org-me-sidebar"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar"),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/org/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: () => {
      toast.success("Logo cargado");
      qc.invalidateQueries({ queryKey: ["org-me"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo subir el logo"),
  });

  const previewOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const previewUrl = slug ? `${previewOrigin}/t/${slug}` : "";
  const subdomainUrl = slug ? `https://${slug}.sprintroll.app` : "";

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("URL copiada");
    } catch { toast.error("No se pudo copiar"); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 512 * 1024) return toast.error("Máximo 512 KB");
    uploadMutation.mutate(f);
    e.target.value = "";
  };

  const normalizedSlug = (slug || "").toLowerCase().replace(/[^a-z0-9-]/g, "");
  const slugChanged = normalizedSlug !== (org?.slug || "");
  const colorChanged = color?.toLowerCase() !== (org?.branding?.primary_color || "").toLowerCase();
  const canSave = (slugChanged || colorChanged) && !saveMutation.isPending && isAdmin;

  const saveAll = () => {
    const body = {};
    if (slugChanged) body.slug = normalizedSlug;
    if (colorChanged) body.primary_color = color;
    if (Object.keys(body).length) saveMutation.mutate(body);
  };

  return (
    <section className="glass-panel rounded-3xl p-6" data-testid="org-branding-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Palette className="h-5 w-5 text-blue-500" /> Marca personalizada
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Tu equipo entra con su propio espacio: logo, color y URL. La marca aparece junto al logotipo de Sprintroll en toda la app.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Left column: identity */}
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Logo del equipo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-16 w-32 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 grid place-items-center overflow-hidden">
                {org?.branding?.logo_url ? (
                  <img src={org.branding.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="branding-logo-preview" />
                ) : (
                  <span className="text-xs text-muted-foreground">Sin logo</span>
                )}
              </div>
              <div>
                <input
                  ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden" onChange={onPickFile}
                  data-testid="branding-logo-file"
                />
                <Button
                  size="sm" variant="outline" disabled={!isAdmin || uploadMutation.isPending}
                  onClick={() => fileRef.current?.click()}
                  data-testid="branding-logo-upload-btn"
                >
                  {uploadMutation.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <><Upload className="h-4 w-4 mr-1.5" /> Subir logo</>}
                </Button>
                <p className="text-[11px] text-muted-foreground mt-1.5">PNG, JPG, WEBP o SVG · máx. 512 KB</p>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Color primario</Label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="color" value={color} onChange={(e) => setColor(e.target.value)}
                disabled={!isAdmin}
                className="h-10 w-14 rounded-lg cursor-pointer border border-slate-300 dark:border-white/10"
                data-testid="branding-color-picker"
              />
              <Input
                value={color} onChange={(e) => setColor(e.target.value)}
                placeholder="#2563EB" maxLength={7} disabled={!isAdmin}
                className="max-w-[140px] font-mono uppercase"
                data-testid="branding-color-input"
              />
              <div className="text-xs text-muted-foreground">Se aplica a botones y acentos.</div>
            </div>
          </div>
        </div>

        {/* Right column: slug + URLs */}
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Slug del espacio</Label>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 flex items-center rounded-lg border border-slate-300 dark:border-white/10 overflow-hidden bg-white/50 dark:bg-slate-900/40">
                <span className="pl-3 pr-1 text-xs text-muted-foreground select-none">sprintroll.app/t/</span>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="empresa-1"
                  disabled={!isAdmin}
                  maxLength={40}
                  className="flex-1 bg-transparent px-1 py-2 text-sm outline-none"
                  data-testid="branding-slug-input"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              3–40 caracteres · letras minúsculas, números y guiones · no puede empezar ni terminar con guión
            </p>
          </div>

          {slug && (
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-3 space-y-2">
              <div className="text-[11px] uppercase font-semibold text-blue-600 dark:text-blue-300 tracking-wider">URLs</div>
              <UrlRow label="Disponible ahora" url={previewUrl} onCopy={copyUrl} copied={copied} testid="branding-url-path" />
              <UrlRow label="Subdominio (próximamente)" url={subdomainUrl} onCopy={copyUrl} copied={copied} testid="branding-url-subdomain" muted />
            </div>
          )}

          {isAdmin && (
            <Button onClick={saveAll} disabled={!canSave} className="w-full" data-testid="branding-save-btn">
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar marca"}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function UrlRow({ label, url, onCopy, copied, testid, muted }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className={`font-mono text-xs truncate ${muted ? "opacity-60" : "text-blue-700 dark:text-blue-200"}`} data-testid={testid}>{url}</div>
      </div>
      <button
        onClick={() => onCopy(url)}
        className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-white/5 transition"
        aria-label="Copiar"
        data-testid={`${testid}-copy`}
      >
        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
      </button>
    </div>
  );
}
